import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { TileWMS } from 'ol/source';
import BaseLayer from 'ol/layer/Base';
import { Geometry } from 'ol/geom';
import Feature from 'ol/Feature';
import { LayerItem, LayerType } from './layerManagement/types';

import { IconType } from 'react-icons';
import {
  PiAnchorSimple,
  PiBoat,
  PiCellSignalSlash,
  PiChartPolar,
  PiCircle,
  PiFolder,
  PiGlobeHemisphereEast,
  PiGridFour,
  PiLineSegment,
  PiMapPin,
  PiNotebook,
  PiPolygon,
  PiQuestion,
} from 'react-icons/pi';
import VectorTileLayer from 'ol/layer/VectorTile';

export const getIconFromLayerType = (layerType: LayerType): IconType => {
  switch (layerType) {
    case 'ships':
      return PiBoat;
    case 'ports':
      return PiAnchorSimple;
    case 'source-identifiers':
    case 'coverage':
      return PiChartPolar;
    case 'report':
      return PiNotebook;
    case 'point':
      return PiMapPin;
    case 'linestring':
      return PiLineSegment;
    case 'polygon':
      return PiPolygon;
    case 'circle':
      return PiCircle;
    case 'wms':
      return PiGlobeHemisphereEast;
    case 'gps-lost-grid':
      return PiCellSignalSlash;
    case 'vectortile':
      return PiGridFour;
    case 'featurecollection':
      return PiFolder;
    default:
      return PiQuestion;
  }
};

export const getTextFromLayerType = (layerType: LayerType): string => {
  switch (layerType) {
    case 'ships':
      return 'Layer that controls ships';
    case 'ports':
      return 'Layer that shows all MDM ports';
    case 'source-identifiers':
      return 'Layer that shows source identifiers such as AtoNs, base stations and receivers';
    case 'report':
      return 'Report Overlay';
    case 'point':
      return 'Point';
    case 'linestring':
      return 'Line';
    case 'polygon':
      return 'Polygon';
    case 'circle':
      return 'Circle';
    case 'wms':
      return 'WMS Layer';
    case 'tile':
      return 'Tile Layer';
    case 'gps-lost-grid':
      return 'GPS Lost Grid';
    case 'vectortile':
      return 'Vector Tile';
    case 'featurecollection':
      return 'Shape Group';
    default:
      return 'Unknown layer';
  }
};

export function getLayerTypeWithShips(layer: LayerItem): LayerType {
  if (layer.name === 'Ships') {
    return 'ships';
  }
  if (layer.name === 'Report Overlay') {
    return 'report';
  }
  if (layer.id === 'gatehouse-ports') {
    return 'ports';
  }
  if (layer.id === 'source-identifiers') {
    return 'source-identifiers';
  }
  if (layer.name.includes('coverage ')) {
    return 'coverage';
  }
  if (layer.name.toLowerCase().includes('gps lost track line')) {
    return 'gps-lost-track-linestring';
  }
  if (layer.name.toLowerCase().includes('gps lost track point')) {
    return 'gps-lost-track-point';
  }

  if (layer.name.toLowerCase().includes('gps lost')) {
    return 'gps-lost-grid';
  }
  return getLayerType(layer.layer);
}

export function getLayerType(layer: BaseLayer): LayerType {
  if (layer instanceof VectorTileLayer) {
    return 'vectortile';
  }

  if (layer instanceof TileLayer) {
    const source = layer.getSource();
    if (source instanceof TileWMS) {
      return 'wms';
    }
    return 'tile';
  }

  if (layer instanceof VectorLayer) {
  const source = layer.getSource();
  if (!source) return 'featurecollection'; // or 'unknown', depending on intent

  const features: Feature<Geometry>[] = source.getFeatures();


    // If there are multiple features, classify as featurecollection
    if (features.length > 1) {
      return 'featurecollection';
    }

    // If there is only one feature, return its geometry type
    if (features.length === 1) {
      const geometryType = features[0].getGeometry()?.getType();
      switch (geometryType) {
        case 'Point':
          return 'point';
        case 'LineString':
          return 'linestring';
        case 'Polygon':
          return 'polygon';
        case 'Circle':
          return 'circle';
        default:
          return 'featurecollection';
      }
    }

    return 'featurecollection';
  }

  return 'unknown';
}
